import { useState } from 'react';
import { Alert, Button, Card, Group, SimpleGrid, Text, rem } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconCheck, IconRefresh, IconX } from '@tabler/icons-react';
import { httpsCallable } from 'firebase/functions';

import { BaseResult, SyncNavRequest } from '../../types';
import { auth, useFirestore, useFunctions } from '../../helpers/firebaseContext';
import { isEtfSectionError, isNavStale, lastBusinessDayString, logEtfEvent, navDateOf, useEtfContext } from './etf_common';

const EtfOperations = () => {
    const { fund, data } = useEtfContext();
    const functions = useFunctions();
    const db = useFirestore();
    const [syncNavLoading, setSyncNavLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const navDate = navDateOf(fund);
    const fundName = fund.name_en || fund.name || fund.id;
    const navStale = isNavStale(fund);

    const syncNav = async () => {
        setSyncNavLoading(true);
        const id = notifications.show({
            loading: true,
            title: `Syncing NAV for ${fundName} (${fund.id})...`,
            message: 'Triggering NAV sync, hang tight.',
            autoClose: false,
            withCloseButton: false,
        });
        const user = auth.currentUser?.email ?? undefined;
        try {
            const syncNavCallable = httpsCallable<SyncNavRequest, BaseResult>(functions, 'syncNav');
            await syncNavCallable({ fundCodes: [fund.id] });
            await logEtfEvent(db, { endpoint: 'syncNav', fund: fund.id, status: 200, source: 'webapp', user });
            notifications.update({
                id,
                color: 'teal',
                title: `NAV sync requested for ${fundName}`,
                message: 'Geneva sync is running in the background. The "NAV landed" event appears in Logs once the fund document updates.',
                icon: <IconCheck style={{ width: rem(18), height: rem(18) }} />,
                loading: false,
                autoClose: 4000,
            });
        } catch (e) {
            console.log(e);
            await logEtfEvent(db, { endpoint: 'syncNav', fund: fund.id, status: 500, source: 'webapp', user, details: { error: e instanceof Error ? e.message : String(e) } });
            notifications.update({
                id,
                color: 'red',
                title: `Failed to sync NAV for ${fundName}`,
                message: e instanceof Error ? e.message : 'Please try again later.',
                icon: <IconX style={{ width: rem(18), height: rem(18) }} />,
                loading: false,
                autoClose: 5000,
            });
        }
        setSyncNavLoading(false);
    };

    //re-pull the latest NAV date's position onto funds/{id}/private/nav_position; the fund listener rebuilds the PCF from there
    const refresh = async () => {
        setRefreshing(true);
        const id = notifications.show({ loading: true, title: `Refreshing position for ${fund.id}...`, message: 'Pulling the position as of the latest NAV date from Geneva.', autoClose: false, withCloseButton: false });
        try {
            const refreshEtfPosition = httpsCallable<{ fundId: string }, { asOf: string | null }>(functions, 'refreshEtfPosition');
            const result = await refreshEtfPosition({ fundId: fund.id });
            notifications.update({ id, color: 'teal', loading: false, autoClose: 4000, title: 'Position refreshed', message: `Position as of ${result.data.asOf ?? 'unknown'} saved. Rebuilding the PCF...` });
        } catch (e) {
            notifications.update({ id, color: 'red', loading: false, autoClose: 6000, title: 'Refresh failed', message: e instanceof Error ? e.message : String(e) });
        }
        setRefreshing(false);
    };

    const pcf = data && !isEtfSectionError(data.pcf) ? data.pcf : null;
    const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 3 });

    return (
        <>
            {navStale && (
                <Alert color="red" variant="filled" icon={<IconAlertTriangle />} title="NAV IS STALE" mb="md">
                    <Text fw={700}>
                        The NAV date ({navDate ?? 'none'}) is older than the last business day ({lastBusinessDayString()}).
                        The external API is returning this data flagged as stale. Sync the NAV before the market opens.
                    </Text>
                </Alert>
            )}
            {!fund.basket_size && (
                <Alert color="orange" icon={<IconAlertTriangle />} title="Basket size not configured" mb="md">
                    Set <code>basket_size</code> on the fund document — the basket and PCF endpoints return an error without it.
                </Alert>
            )}
            {!fund.etf_cutoff && (
                <Alert color="orange" icon={<IconAlertTriangle />} title="Cut-off not configured" mb="md">
                    Set <code>etf_cutoff</code> (e.g. <code>"14:00"</code>, Kuwait time) on the fund document — creation/redemption requests cannot be sent to KCC without it.
                </Alert>
            )}
            <Group mb="md">
                <Button loading={syncNavLoading} onClick={syncNav}>Sync NAV</Button>
                <Button variant="default" leftSection={<IconRefresh size={16} />} loading={refreshing} onClick={refresh}>Refresh Data</Button>
            </Group>

            {!data && <Text c="dimmed">Waiting for the synced position — click <b>Refresh Data</b> if none has been pulled yet.</Text>}

            {pcf && (
                <Card withBorder padding="md">
                    <Text fw={700} mb="xs">Cash per creation unit (position as of {pcf.positionAsOf ?? '?'}, ratio {(pcf.sharesPerCreationUnit / pcf.sharesOutstanding).toFixed(6)})</Text>
                    <SimpleGrid cols={{ base: 2, md: 5 }} mb="sm">
                        <div>
                            <Text size="xs" c="dimmed">Fund cash (pro-rata)</Text>
                            <Text fw={600}>{fmt(pcf.cashPerCreationUnit)} {pcf.baseCurrency}</Text>
                        </div>
                        <div>
                            <Text size="xs" c="dimmed">Dividends due (not credited)</Text>
                            <Text fw={600}>{fmt(pcf.dividendDuePerCreationUnit)} {pcf.baseCurrency}</Text>
                        </div>
                        <div>
                            <Text size="xs" c="dimmed">Share rounding + accruals (balancing)</Text>
                            <Text fw={600}>{fmt(pcf.estimatedCashPerCreationUnit - pcf.cashPerCreationUnit - pcf.dividendDuePerCreationUnit)} {pcf.baseCurrency}</Text>
                        </div>
                        <div>
                            <Text size="xs" c="dimmed">Estimated cash per CU (published: NAV {pcf.navDate ?? '?'} × CU − basket)</Text>
                            <Text fw={600} c={pcf.estimatedCashPerCreationUnit < 0 ? 'red' : undefined}>{fmt(pcf.estimatedCashPerCreationUnit)} {pcf.baseCurrency}</Text>
                        </div>
                        <div>
                            <Text size="xs" c="dimmed">Bonus shares pending (lines)</Text>
                            <Text fw={600}>{pcf.constituents.filter((c) => c.bonusSharesPending).length}</Text>
                        </div>
                    </SimpleGrid>
                    <Text size="sm" c="dimmed">
                        The published cash is the balancing amount (KCC's cash component): NAV × creation unit minus the basket, so it covers the
                        fund's cash and dividends due plus whole-share rounding (basket quantities round up) and accruals. Requests book the same rule.
                        {pcf.navPositionDateMismatch ? ` The position (${pcf.positionAsOf ?? '?'}) is not the NAV date (${pcf.navDate ?? '?'}) — Refresh Data before relying on it.` : ''}
                    </Text>
                </Card>
            )}
        </>
    );
};

export default EtfOperations;
